import { Box } from "@modernatx-internal/component-library";
import React from "react";

import { FinderFooterContent } from "./FinderFooterContent";

export interface FinderFooterBlockProps {
  mainContent: {
    links: NavLinkProps[];
    title: string;
  }[];
  locale?: string;
  subSection?: {
    documentId: string;
    links: NavLinkProps[];
  };
}

interface NavLinkProps {
  external?: boolean;
  href?: string;
  target?: "_blank";
  text: string[];
  title?: string;
  action?: "openCookieConsent";
}

export const EXPAND_ICON_ID = "finder-footer-expand-icon";

export const DesktopFooter: React.FC<FinderFooterBlockProps> = ({
  mainContent,
  subSection,
  locale = "en-US"
}) => {
  const [expanded, expandedSet] = React.useState(false);
  const [hasInitialized, hasInitializedSet] = React.useState(false);
  const footerContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (footerContainerRef.current && !hasInitialized) {
      if (!hasInitialized && footerContainerRef.current) {
        hasInitializedSet(true);
      }
    }
  }, [footerContainerRef, hasInitialized]);

  const handleExpandClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!expanded) {
      expandedSet(true);
    } else {
      const id = event.currentTarget.getAttribute("id");
      if (id === EXPAND_ICON_ID) {
        expandedSet(false);
      }
    }
  };

  return (
    <Box
      ref={footerContainerRef}
      id="footer"
      sx={{
        // this token can be changed later when we get the versioning upgraded to latest ui-kit. Color-Brand-Moderna-X tokens aren't exported in this old version.
        backgroundColor: "onSurface-brand-primary-click",
        bottom: 0,
        color: "white",
        display: ["none", null, "flex"],
        flexDirection: "column",
        fontSize: "font-size-4",
        fontStyle: "normal",
        fontWeight: "font-weight-medium",
        gap: [10, 10, 10],
        left: 0,
        letterSpacing: "kerning-1",
        lineHeight: "initial",
        position: "absolute",
        px: "spacing-8",
        py: expanded ? "spacing-5" : "spacing-3",
        transform: expanded ? "translateY(0px)" : "translateY(calc(100% - 56px))",
        transition: () =>
          hasInitialized
            ? "transform 400ms cubic-bezier(0.4, 0, 0.06, 1), padding-top 400ms cubic-bezier(0.4, 0, 0.06, 1), padding-bottom 400ms cubic-bezier(0.4, 0, 0.06, 1)"
            : "transform 100ms ease-out, padding-top 100ms ease-out, padding-bottom 100ms ease-out",
        // hide footer until we've initialized it by calculating the height of the retracted footer, and transitioning it into that position.
        visibility: hasInitialized ? "visible" : "hidden",
        width: "100%",
        ":hover": { cursor: expanded ? "default" : "pointer" },
        zIndex: "panel"
      }}
      onClick={handleExpandClick}
    >
      <FinderFooterContent
        expanded={expanded}
        handleExpandClick={handleExpandClick}
        locale={locale}
        mainContent={mainContent}
        subSection={subSection}
      />
    </Box>
  );
};
